import { h } from 'preact';
import Store from 'store/store';
import { navigateInternally } from 'utils/bolt';
import { smoothScrollTo } from 'utils/smoothScroll';
import { sortObjectByOrder, joinClasses, debounce } from 'utils/utils';

import Section from 'components/bolt/section/section';
import ArrowDown from 'components/bolt/icons/arrowDown';
import HTML from 'components/core/html/html';

import s from 'components/bolt/toutHorizontal/toutHorizontal.sss';

export default class ToutHorizontal extends Section {
	constructor(props) {
		super(props);

		this.state = {
			usingScrollWheel: true,
			touts: this.getTouts(props),
			infiniteScroll: true,
			mobileToutCount: 1,
			desktopToutCount: 3,
			canScrollLeft: true,
			canScrollRight: true,
		};

		this.onToutsContainerScroll = debounce(this.onToutsContainerScroll.bind(this), 20);
		this.move = this.move.bind(this);
		this.snapToClosestTout = this.snapToClosestTout.bind(this);
		this.setUsingScrollWheel = this.setUsingScrollWheel.bind(this);
		this.setNotUsingScrollWheel = this.setNotUsingScrollWheel.bind(this);
	}

	componentDidMount() {
		super.componentDidMount();

		this.onToutsContainerScroll();
		window.addEventListener('resize', this.onToutsContainerScroll);
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.id !== this.props.id) {
			this.setState({ touts: this.getTouts(nextProps) });
		}
	}

	componentWillUnmount() {
		window.clearTimeout(this.snapTimeout);
		window.removeEventListener('resize', this.onToutsContainerScroll);
	}

	getPageData(props = this.props) {
		return props.pageData
		|| props?.appData?.pages?.[props.id]?.data
		|| {};
	}

	getTouts(props = this.props) {
		return sortObjectByOrder(this.getPageData(props).carousel, (tout, key) => ({ ...tout, key }));
	}

	fixInfiniteScroll() {
		if (!this.state.infiniteScroll) return;

		const el = this.$toutsContainer;
		if (!el) return;

		const maxScroll = el.scrollWidth;
		const repeatingSize = maxScroll / 3;
		const currentPos = el.scrollLeft + el.offsetWidth * 0.5;

		if (currentPos < repeatingSize) {
			el.scrollLeft += repeatingSize;
		} else if (currentPos > maxScroll - repeatingSize) {
			el.scrollLeft -= repeatingSize;
		}
		const leftEdge = el.scrollLeft;
		const rightEdge = el.scrollLeft + el.offsetWidth;

		[...el.children].forEach(child => {
			const childLeft = child.offsetLeft;
			const childRight = child.offsetLeft + child.offsetWidth;
			const isVisible = childRight > leftEdge && childLeft < rightEdge;
			if (isVisible) {
				child.removeAttribute('inert');
			} else {
				child.setAttribute('inert', '');
			}
		});
	}


	onToutsContainerScroll() {
		clearTimeout(this.snapTimeout);
		if (!this.state.infiniteScroll) {
			let el = this.$toutsContainer;
			let tolerance = 16;
			this.setState({ canScrollLeft: el.scrollLeft > tolerance, canScrollRight: el.scrollLeft < el.scrollWidth - el.offsetWidth - tolerance });
		}
		if (this.scrollingTo) {
			return;
		}
		this.fixInfiniteScroll();
		if (this.state.usingScrollWheel) {
			this.snapTimeout = setTimeout(this.snapToClosestTout, 500);
		}
	}

	getClosestChild(parent, position) {
		let closest;
		let closestDist = Infinity;
		let scroll = parent.scrollLeft;
		[...parent.children].find(el => {
			let elCenter = el.offsetLeft + el.offsetWidth * .5 - scroll;
			let dist = Math.abs(elCenter - position);
			if (dist >= closestDist) {
				return true;
			}
			closest = el;
			closestDist = dist;
		});
		return closest;
	}

	checkMarkdownLinkClick(e) {
		let link = e.target.closest('a');
		if (!link) {
			return;
		}
		let href = link.getAttribute('href');
		if (href.startsWith('#$')) {
			e.preventDefault();
			let page = href.slice(2).trim();
			let navOptions = {};
			if (page.includes('/?')) {
				let [pageId, qs] = page.split('/?');
				page = pageId;
				navOptions.queryString = '?' + qs;
			}
			navigateInternally(page, navOptions);
		}
		let trackAction = link.getAttribute('data-track-action');
		if (trackAction) {
			window.firePowsterCustomEvent?.(trackAction, link.getAttribute('data-track-category') || 'tout', link.getAttribute('data-track-label'));
		}
	}

	scrollTo(destination, checkScrollPositionAfter) {
		if (!destination && destination !== 0) {
			return;
		}
		clearTimeout(this.snapTimeout);
		smoothScrollTo(
			this.$toutsContainer,
			destination,
			200,
			{
				direction: 'horizontal',
				relativeTo: 'middle',
				callback: () => {
					this.scrollingTo = null;
					if (checkScrollPositionAfter) {
						setTimeout(this.onToutsContainerScroll, 0);
					}
				}
			}
		);
		this.scrollingTo = destination;
	}

	snapToClosestTout() {
		let container = this.$toutsContainer;
		const halfWidth = container.children[0]?.offsetWidth * .5 || 0;
		if (container.scrollLeft < halfWidth) {
			this.scrollTo(0);
		} else if (container.scrollLeft > container.scrollWidth - container.offsetWidth - halfWidth) {
			this.scrollTo(container.scrollWidth - container.offsetWidth);
		} else {
			this.scrollTo(this.getClosestChild(container, container.offsetWidth * .5));
		}
	}

	setUsingScrollWheel() {
		this.setState({ usingScrollWheel: true });
	}
	setNotUsingScrollWheel() {
		this.setState({ usingScrollWheel: false });
	}

	move(direction) {
		let el = this.$toutsContainer;
		if (!el) {
			return;
		}
		this.fixInfiniteScroll();
		// let currentElement = this.scrollingTo || this.getClosestChild(el, el.offsetWidth * .5);
		let currentElement = this.getClosestChild(el, el.offsetWidth * .5);
		if (!currentElement) {
			return;
		}
		this.scrollTo(direction < 0 ? currentElement.previousElementSibling : currentElement.nextElementSibling, true);
	}

	trackToutClick(data) {
		const label = data?.trackingLabel || `${data?.title} - ${data?.button}`;
		Store.emit('TOUT_CLICK', 'horizontal', 'tout', label);
	}
	triggerButtonClick(data) {
		const { path } = data;
		this.trackToutClick(data);
		navigateInternally(path, { animate: true });
	}

	renderTout(data, index, keySuffix) {
		const { title, description, src, alt, button } = data;
		const imageOnly = !title && !description && !button;

		let buttonElement;
		if (button) {
			if (/^(https?:)?\/\//.test(data.path) || /^mailto:/.test(data.path)) {
				buttonElement = (
					<a class={joinClasses(s.button, 'mediumB')} href={data.path} target={!data.sameWindow && '_blank'} onClick={() => this.trackToutClick(data)}>
						{button}
					</a>
				);
			} else {
				buttonElement = (
					<button class={joinClasses(s.button, 'mediumB')} onClick={() => this.triggerButtonClick(data)}>
						{button}
					</button>
				);
			}
		}

		const isDuplicate = !!keySuffix?.startsWith('duplicate');
		return (
			<div class={s.tout} aria-hidden={isDuplicate && 'true'} inert={isDuplicate} key={(data.key || index) + (keySuffix || '')}>
				<div class={s.toutContent}>
					<div class={s.toutStuff}>
						{src && <img crossOrigin="anonymous" class={joinClasses(s.image, imageOnly && s.imageOnly)} alt={alt} src={src} />}
						{title && <HTML tag="h3" class={joinClasses(s.toutTitle, 'largeB')} onClickCapture={this.checkMarkdownLinkClick} content={title} markdown inline />}
						{description && (
							<HTML
								tag="h4"
								class={joinClasses(s.toutDescription, s.showGradient, button && s.flex, 'large')}
								content={description}
								markdown
							/>
						)}
						{buttonElement}
					</div>
				</div>
			</div>
		);
	}

	renderTouts() {
		const { touts, mobileToutCount, desktopToutCount } = this.state;
		const displayedTouts = this.props.client?.deviceWidthSize === 'mobile' ? mobileToutCount : desktopToutCount;
		const tooManyTouts = touts.length > displayedTouts;

		let content = touts.map((tout, index) => this.renderTout(tout, index));
		const infinite = tooManyTouts && this.state.infiniteScroll;
		if (tooManyTouts && this.state.infiniteScroll) {
			// Render duplicate items for infinite scrolling
			content = [
				...touts.map((tout, index) => this.renderTout(tout, index, 'duplicate1')),
				...content,
				...touts.map((tout, index) => this.renderTout(tout, index, 'duplicate2')),
			];
		}

		return (
			<div class={joinClasses(s.toutsWrapper, infinite && s.infinite)} key="touts">
				<div
					class={s.toutsContainer}
					onScroll={this.onToutsContainerScroll}
					onMouseWheel={this.setUsingScrollWheel}
					onTouchStart={this.setNotUsingScrollWheel}
					onKeyDown={this.setNotUsingScrollWheel}
					tabIndex="0"
					ref={e => this.$toutsContainer = e}
				>
					{content}
				</div>
				{tooManyTouts && this.renderArrows()}
			</div>
		);
	}

	renderArrows() {
		return (
			<div class={s.arrows}>
				<button class={joinClasses(s.arrow, s.left)} onClick={() => this.move(-1)} disabled={!this.state.canScrollLeft}>
					<ArrowDown />
				</button>
				<button class={joinClasses(s.arrow, s.right)} onClick={() => this.move(1)} disabled={!this.state.canScrollRight}>
					<ArrowDown />
				</button>
			</div>
		);
	}

	renderTitle() {
		const { title } = this.getPageData();
		return title && <HTML tag="h3" class={joinClasses(s.title, 'h3')} content={title} onClickCapture={this.checkMarkdownLinkClick} markdown inline key="title" />;
	}

	renderDescription() {
		const { description } = this.getPageData();
		return description && <HTML tag="div" class={joinClasses(s.description, 'xlarge')} content={description} markdown key="description" />;
	}

	render({ id }) {
		const isSection = this.props?.appData?.pages?.[id]?.section || false;

		return (
			<div class={joinClasses(s.wrapper, isSection && s.section, this.state.usingScrollWheel && s.usingScrollWheel)} data-section={id}>
				{this.renderBgContent()}
				{this.renderTitle()}
				{this.renderDescription()}
				{this.renderTouts()}
				{this.renderOverlayedContent()}
			</div>
		);
	}
}
